import { useState, useEffect, useMemo, useCallback } from 'react';
import { Event } from '@/types/zod.schemas';
import { useHistoryStore } from '@stores/v4/useHistoryStore';
import { useUIStore } from '@stores/v4/useUIStore';
import { useSupplementStore } from '@stores/v4/useSupplementStore';
import { useMetabolicStore } from '@stores/v4/useMetabolicStore';
import { useSessionStore } from '@stores/v4/useSessionStore';
import { useComposerStore } from '@stores/v4/useComposerStore';
import { HistoryCompute } from '@/shared/logic/history-logic';
import { EVENT_TYPES } from '@/shared/constants/event-types.v4';
import { toLogicDate } from '@/utils/date-utils';

/**
 * useMissionBoardState: Headless logic for the Master Mission Board.
 * REFACTORED: Moved filtering logic out of store actions to ensure React reactivity.
 * HARDENED: Smart event selection (Active sessions vs Drawer menu).
 * FIXED: Explicitly defined local isDrawerOpen and isMenuOpen state to resolve ReferenceErrors.
 * ENHANCED: Merges Suggested Supplementation ghosts with lifecycle and manual dismissal.
 */
export const useMissionBoardState = () => {
    // 1. UI STORE (Navigation & Date)
    const lastSelectedDate = useUIStore(state => state.lastSelectedDate);
    const setLastSelectedDate = useUIStore(state => state.setLastSelectedDate);
    const maximizeSession = useUIStore(state => state.maximizeSession);
    const maximizeRecovery = useUIStore(state => state.maximizeRecovery);

    // 2. DATA STORES
    const allEvents = useHistoryStore(state => state.events);
    const activeHistorySession = useHistoryStore(state => state.activeHistorySession);
    const fetchGlobalHistory = useHistoryStore(state => state.fetchGlobalHistory);

    const { getDailyProjections, dismissedSuggestions, dismissSuggestion } = useSupplementStore();
    const { dailySnapshot } = useMetabolicStore();
    const { activeSession } = useSessionStore();
    const { openComposer } = useComposerStore();
    
    // Lazy access to RecoveryStore to avoid circular dependency
    const activeRecoverySession = (window as any).useRecoveryStore?.getState()?.activeRecoverySession;

    // 3. INTERNAL UI STATE
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedPlannedEvent, setSelectedPlannedEvent] = useState<Event | null>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const selectedDate = useMemo(() => 
        lastSelectedDate ? new Date(lastSelectedDate) : new Date(), 
    [lastSelectedDate]);

    // Background Sync (Hydrate from DB on mount)
    useEffect(() => {
        // Trigger a background fetch to sync with DB
        // Passing false (or nothing) ensures it's a silent background sync
        fetchGlobalHistory(); 
    }, [fetchGlobalHistory]);

    // Ensure Protocol is fetched for ghost projection
    const { activeProtocol, fetchActiveProtocol } = useSupplementStore();
    useEffect(() => {
        // Force a fetch if there is no protocol OR if the protocol exists but has no items (stale cache on mobile PWAs)
        if (!activeProtocol || !activeProtocol.items || activeProtocol.items.length === 0) {
            fetchActiveProtocol();
        }
    }, [activeProtocol?.id, fetchActiveProtocol]); // Safely depend on ID to prevent infinite loops while catching staleness

    // --- CORE LOGIC: Reactive Event Filtering ---
    const realEvents = useMemo(() => {
        return HistoryCompute.getEventsForDate(allEvents, selectedDate, activeSession, activeRecoverySession);
    }, [allEvents, selectedDate, activeSession, activeRecoverySession]);

    // Suggested Supplements (Ghosts)
    const projections = useMemo(() => 
        getDailyProjections(selectedDate, realEvents, dailySnapshot), 
    [selectedDate, realEvents, dailySnapshot, getDailyProjections]);

    const events = useMemo(() => {
        const list = [...realEvents];
        const todayStr = toLogicDate(new Date());
        const selectedStr = toLogicDate(selectedDate);
        const now = new Date();

        const ghosts = projections
            .filter(p => !p.isCrystallized)
            .filter(p => {
                const dismissId = `${selectedStr}:${p.window}`;
                if (dismissedSuggestions.includes(dismissId)) return false;
                if (selectedStr < todayStr) return false; 
                if (selectedStr === todayStr) {
                    const diffMs = now.getTime() - p.scheduledTime.getTime();
                    return diffMs < (12 * 60 * 60 * 1000); // 12h expiry
                }
                return true; 
            })
            .map(p => ({
                id: `ghost-${p.window}`,
                label: `SUGGESTED: ${p.label}`,
                type: EVENT_TYPES.SUPPLEMENTATION,
                status: 'GHOST', 
                start_time: p.scheduledTime.toISOString(),
                payload: { window: p.window },
                isGhost: true
            } as any));

        return [...list, ...ghosts].sort((a, b) => {
            const timeA = new Date(a.start_time).getTime();
            const timeB = new Date(b.start_time).getTime();
            return timeB - timeA; 
        });
    }, [realEvents, projections, selectedDate, dismissedSuggestions]);

    // --- HANDLERS ---
    const setSelectedDate = useCallback((date: Date) => {
        setLastSelectedDate(date.toISOString());
    }, [setLastSelectedDate]);

    const handleSelectEvent = useCallback((event: Event) => {
        // 1. GHOST INTERACTION
        if ((event as any).isGhost) {
            const windowName = (event.payload as any)?.window || 'CUSTOM';
            openComposer(selectedDate, { 
                type: EVENT_TYPES.SUPPLEMENTATION, 
                window: windowName,
                start: event.start_time,
                status: 'COMPLETED',
                label: windowName.replace('_', ' ') + " STACK"
            });
            return;
        }

        // 2. ACTIVE SESSION INTERACTION
        if (event.status === 'ACTIVE') {
            if (event.type === EVENT_TYPES.PERFORMANCE || event.type === EVENT_TYPES.MOBILITY) {
                maximizeSession();
                if ((window as any).onNavigate) (window as any).onNavigate('session');
                return;
            }
            if (event.type === EVENT_TYPES.RECOVERY) {
                maximizeRecovery();
                if ((window as any).onNavigate) (window as any).onNavigate('session');
                return;
            }
        }

        // 3. PLANNED/COMPLETED -> OPEN COMPOSER DIRECTLY (Replaces Drawer Menu)
        openComposer(event);
    }, [openComposer, selectedDate, maximizeSession, maximizeRecovery]);

    const handleDismissGhost = useCallback((window: string) => {
        dismissSuggestion(toLogicDate(selectedDate), window);
    }, [selectedDate, dismissSuggestion]);

    const closeDrawer = useCallback(() => {
        setIsDrawerOpen(false);
        setSelectedPlannedEvent(null);
    }, []);

    return {
        selectedDate,
        setSelectedDate,
        currentMonth,
        setCurrentMonth,
        selectedPlannedEvent,
        isDrawerOpen,
        setIsDrawerOpen,
        isMenuOpen,
        setIsMenuOpen,
        projections,
        events,
        allEvents,
        activeHistorySession,
        handleSelectEvent,
        handleDismissGhost,
        closeDrawer
    };
};
